/*
  Header navigation behaviour for sections/wl-header.liquid.

  <wl-nav-drawer> (snippets/wl-nav-drawer.liquid): the phone menu and search screen,
  built on the theme's DrawerElement (assets/theme.js), which already gives it the focus
  trap, Escape, the scroll lock and the move to <body> while open. The hamburger opens the
  menu; the phone search icon carries data-wl-view="search" and opens the search screen.

  [data-wl-account] (sections/wl-header.liquid): the desktop account dropdown. A mouse
  opens it by hovering the icon; a tap, a click that did not start as a hover, Enter or
  Space toggle it; Escape, a click outside and focus leaving close it.

  PostHog: clicks carry data-wl-cta; opens and submits are announced as wl-nav:* events on
  document. assets/analytics-posthog-funnel.js names both.
*/
(function () {
  'use strict';

  var RECENT_KEY = 'wl_recent_searches';
  var RECENT_MAX = 5;
  var CLOCK_ICON =
    '<svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true" focusable="false">' +
    '<circle cx="8" cy="8" r="5.75" stroke="#938E88" stroke-width="1.5"/>' +
    '<path d="M8.6 5.1v2.8M8.81 7.75l1.59.85" stroke="#938E88" stroke-width="1.2" stroke-linecap="round"/></svg>';
  var SEARCH_ICON =
    '<svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true" focusable="false">' +
    '<circle cx="7" cy="7" r="4.75" stroke="#938E88" stroke-width="1.5"/>' +
    '<path d="M10.5 10.5l3 3" stroke="#938E88" stroke-width="1.5" stroke-linecap="round"/></svg>';
  var SUGGEST_MIN = 2;
  var SUGGEST_DELAY = 250;

  function searchHref(base, term) {
    return base + '?q=' + encodeURIComponent(term) + '&options%5Bprefix%5D=last';
  }

  function formatPrice(value) {
    if (value == null || value === '') return '';
    try {
      if (window.theme && theme.Currency && theme.settings && theme.settings.moneyFormat) {
        return String(theme.Currency.formatMoney(String(value), theme.settings.moneyFormat)).replace(/<[^>]*>/g, '');
      }
    } catch (e) {}
    return '$' + value;
  }

  function thumbUrl(src) {
    if (!src) return '';
    try {
      var url = new URL(src, window.location.origin);
      url.searchParams.set('width', '96');
      return url.toString();
    } catch (e) {
      return src;
    }
  }

  function announce(name, detail) {
    document.dispatchEvent(new CustomEvent('wl-nav:' + name, { detail: detail || {} }));
  }

  function reducedMotion() {
    return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  // Storage can be blocked (private windows, strict settings); the screen still works without history.
  function readRecent() {
    try {
      var list = JSON.parse(window.localStorage.getItem(RECENT_KEY) || '[]');
      if (!Array.isArray(list)) return [];
      return list.filter(function (term) { return typeof term === 'string' && term.trim(); }).slice(0, RECENT_MAX);
    } catch (e) {
      return [];
    }
  }

  function writeRecent(list) {
    try {
      window.localStorage.setItem(RECENT_KEY, JSON.stringify(list.slice(0, RECENT_MAX)));
    } catch (e) {}
  }

  function rememberSearch(term) {
    term = String(term || '').replace(/\s+/g, ' ').trim().slice(0, 80);
    if (!term) return;
    var lower = term.toLowerCase();
    var list = readRecent().filter(function (t) { return t.toLowerCase() !== lower; });
    list.unshift(term);
    writeRecent(list);
  }

  function forgetSearch(term) {
    var lower = String(term || '').toLowerCase();
    writeRecent(readRecent().filter(function (t) { return t.toLowerCase() !== lower; }));
  }

  function waitForTransition(el, fallbackMs) {
    return new Promise(function (resolve) {
      var done = false;
      function finish() {
        if (done) return;
        done = true;
        resolve();
      }
      el.addEventListener('transitionend', function onEnd(event) {
        if (event.target !== el) return;
        el.removeEventListener('transitionend', onEnd);
        finish();
      });
      setTimeout(finish, fallbackMs);
    });
  }

  function defineDrawer() {
    var Base = window.customElements.get('drawer-element');
    if (!Base || window.customElements.get('wl-nav-drawer')) return;

    class WlNavDrawer extends Base {
      constructor() {
        super();
        this.view = 'menu';
        // On the element itself, not in connectedCallback: DrawerElement moves the drawer to
        // <body> and back on every open, which reconnects it each time.
        this.addEventListener('click', this.onClick.bind(this));
        this.addEventListener('submit', this.onSubmit.bind(this));
        this.addEventListener('input', this.onInput.bind(this));
        this.addEventListener('keydown', this.onKeydown.bind(this));
        this.onFocusIn = this.onFocusIn.bind(this);
      }

      // The theme's trap (theme.a11y.trapFocus) takes its first and last stops from every
      // focusable in the element, the hidden view and closed panels included, so Tab ran off
      // the last visible link onto the page behind. This drawer keeps its own trap over what is
      // on screen at the moment of the key press.
      get focusables() {
        return Array.from(this.querySelectorAll('a[href], button:not([disabled]), input:not([type="hidden"]):not([disabled]), [tabindex]:not([tabindex^="-"])')).filter(function (el) {
          return el.getClientRects().length > 0 && !el.closest('[hidden]');
        });
      }

      afterShow() {
        super.afterShow();
        if (window.theme && theme.a11y) theme.a11y.removeTrapFocus();
        document.addEventListener('focusin', this.onFocusIn);
      }

      onKeydown(event) {
        if (event.key !== 'Tab' || !this.open) return;
        var items = this.focusables;
        if (!items.length) return;
        var first = items[0];
        var last = items[items.length - 1];
        var active = document.activeElement;
        if (event.shiftKey && (active === first || !this.contains(active))) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && (active === last || !this.contains(active))) {
          event.preventDefault();
          first.focus();
        }
      }

      onFocusIn(event) {
        if (!this.open || this.contains(event.target)) return;
        var items = this.focusables;
        if (items.length) items[0].focus();
      }

      get inner() {
        return this.querySelector('.wl-mdrawer__inner');
      }

      get searchInput() {
        return this.querySelector('[data-wl-search-input]');
      }

      get focusElement() {
        if (this.view === 'search' && this.searchInput) return this.searchInput;
        return this.querySelector('[data-wl-drawer-close]') || this;
      }

      show(activeElement, animate) {
        var wantsSearch = !!(activeElement && activeElement.getAttribute && activeElement.getAttribute('data-wl-view') === 'search');
        if (!this.open) this.setView(wantsSearch ? 'search' : 'menu');
        var shown = super.show(activeElement, animate);
        // iOS only raises the keyboard for a focus made inside the tap itself, so the field is
        // focused here, while the drawer is already visible, not after the fade.
        if (wantsSearch && this.searchInput) {
          try { this.searchInput.focus({ preventScroll: true }); } catch (e) { this.searchInput.focus(); }
        }
        return shown;
      }

      // The theme's drawers wait for an overlay's transition; this one fades its own panel.
      showTransition(animate) {
        var inner = this.inner;
        var self = this;
        if (inner) void inner.offsetWidth;
        this.setAttribute('active', '');
        if (animate === false || !inner || reducedMotion()) return Promise.resolve();
        return waitForTransition(inner, 350).then(function () { return self; });
      }

      hideTransition() {
        var inner = this.inner;
        this.removeAttribute('active');
        if (!inner || reducedMotion()) return Promise.resolve();
        return waitForTransition(inner, 350);
      }

      afterHide() {
        super.afterHide();
        // The theme resolves the hide after its transition even when the drawer was opened again
        // inside it; resetting then would throw away the view the new open just set.
        if (this.open) return;
        document.removeEventListener('focusin', this.onFocusIn);
        this.reset();
      }

      setView(view, moveFocus) {
        this.view = view === 'search' ? 'search' : 'menu';
        this.setAttribute('data-view', this.view);
        var self = this;
        this.querySelectorAll('[data-wl-view-panel]').forEach(function (panel) {
          panel.hidden = panel.getAttribute('data-wl-view-panel') !== self.view;
        });
        if (this.view === 'search') this.renderRecent();
        var scroller = this.querySelector('[data-wl-drawer-scroll]');
        if (scroller) scroller.scrollTop = 0;
        if (moveFocus && this.open) {
          var target = this.focusElement;
          if (target && target.focus) target.focus();
        }
      }

      reset() {
        this.closePanels();
        this.querySelectorAll('[data-wl-drawer-acc]').forEach(function (button) {
          var open = button.hasAttribute('data-wl-default-open');
          button.setAttribute('aria-expanded', open ? 'true' : 'false');
          var list = document.getElementById(button.getAttribute('aria-controls'));
          if (list) list.hidden = !open;
        });
        if (this.searchInput) this.searchInput.value = '';
        this.renderSuggestions('', null);
        this.setView('menu');
      }

      closePanels() {
        this.querySelectorAll('[data-wl-drawer-parent][aria-expanded="true"]').forEach(function (button) {
          button.setAttribute('aria-expanded', 'false');
          var panel = document.getElementById(button.getAttribute('aria-controls'));
          if (panel) panel.hidden = true;
          var item = button.closest('li');
          if (item) item.classList.remove('is-open');
        });
        this.removeAttribute('data-panel-open');
      }

      // The row hides itself once its panel is open (the back arrow is the way out), so focus is
      // handed to the back arrow, a frame later so the new state is on screen. Every input gets it:
      // a screen reader's double-tap arrives as an ordinary click.
      togglePanel(button) {
        var open = button.getAttribute('aria-expanded') !== 'true';
        this.closePanels();
        if (open) {
          button.setAttribute('aria-expanded', 'true');
          var panel = document.getElementById(button.getAttribute('aria-controls'));
          if (panel) panel.hidden = false;
          var item = button.closest('li');
          if (item) item.classList.add('is-open');
          this.setAttribute('data-panel-open', '');
          announce('drawer-panel', { panel: button.getAttribute('data-wl-panel') || null });
          var back = this.querySelector('[data-wl-drawer-back]');
          if (back) requestAnimationFrame(function () { back.focus(); });
        }
        var scroller = this.querySelector('[data-wl-drawer-scroll]');
        if (scroller) scroller.scrollTop = 0;
      }

      toggleAccordion(button) {
        var open = button.getAttribute('aria-expanded') !== 'true';
        button.setAttribute('aria-expanded', open ? 'true' : 'false');
        var list = document.getElementById(button.getAttribute('aria-controls'));
        if (list) list.hidden = !open;
        if (open) announce('drawer-accordion', { column: button.getAttribute('data-wl-column') || null });
      }

      renderRecent() {
        var wrap = this.querySelector('[data-wl-recent]');
        var list = wrap && wrap.querySelector('[data-wl-recent-list]');
        var form = this.querySelector('[data-wl-search-form]');
        if (!wrap || !list) return;
        var base = (form && form.getAttribute('action')) || '/search';
        var terms = readRecent();
        list.textContent = '';
        terms.forEach(function (term) {
          var item = document.createElement('li');
          item.className = 'wl-msearch__recent-item';
          item.innerHTML = CLOCK_ICON;
          var link = document.createElement('a');
          link.className = 'wl-msearch__recent-link';
          link.href = searchHref(base, term);
          link.textContent = term;
          link.setAttribute('data-wl-cta', 'nav-search-recent');
          var remove = document.createElement('button');
          remove.type = 'button';
          remove.className = 'wl-msearch__remove';
          remove.setAttribute('aria-label', 'Remove ' + term);
          remove.setAttribute('data-wl-recent-remove', term);
          remove.textContent = '\u00d7';
          item.appendChild(link);
          item.appendChild(remove);
          list.appendChild(item);
        });
        wrap.hidden = terms.length === 0;
      }

      onInput(event) {
        var input = event.target;
        if (!input || !input.matches || !input.matches('[data-wl-search-input]')) return;
        clearTimeout(this.suggestTimer);
        var term = input.value.replace(/\s+/g, ' ').trim();
        if (term.length < SUGGEST_MIN) {
          if (this.suggestAbort) this.suggestAbort.abort();
          this.renderSuggestions(term, null);
          return;
        }
        var self = this;
        this.suggestTimer = setTimeout(function () { self.fetchSuggestions(term); }, SUGGEST_DELAY);
      }

      // The store's predictive search (the same endpoint the theme's search drawer reads), as JSON.
      fetchSuggestions(term) {
        if (this.suggestAbort) this.suggestAbort.abort();
        var controller = typeof AbortController === 'function' ? new AbortController() : null;
        this.suggestAbort = controller;
        var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
        var params = new URLSearchParams();
        params.set('q', term);
        params.set('resources[type]', 'product,query');
        params.set('resources[limit]', '4');
        params.set('resources[limit_scope]', 'each');
        params.set('resources[options][unavailable_products]', 'last');
        var self = this;
        fetch(root + 'search/suggest.json?' + params.toString(), {
          headers: { Accept: 'application/json' },
          signal: controller ? controller.signal : undefined,
        })
          .then(function (response) {
            if (!response.ok) throw new Error('suggest ' + response.status);
            return response.json();
          })
          .then(function (data) {
            if (!self.searchInput || self.searchInput.value.replace(/\s+/g, ' ').trim() !== term) return;
            self.renderSuggestions(term, (data && data.resources && data.resources.results) || {});
          })
          .catch(function (error) {
            if (error && error.name === 'AbortError') return;
            if (self.searchInput && self.searchInput.value.replace(/\s+/g, ' ').trim() === term) self.renderSuggestions(term, {});
          });
      }

      renderSuggestions(term, results) {
        var box = this.querySelector('[data-wl-search-results]');
        var view = this.querySelector('[data-wl-view-panel="search"]');
        var status = this.querySelector('[data-wl-search-status]');
        var form = this.querySelector('[data-wl-search-form]');
        if (!box || !view) return;
        box.textContent = '';
        if (!results) {
          box.hidden = true;
          view.removeAttribute('data-searching');
          if (status) status.textContent = '';
          return;
        }
        var base = (form && form.getAttribute('action')) || '/search';
        var queries = (results.queries || []).slice(0, 3);
        var products = (results.products || []).slice(0, 4);

        function row(href, cta, className) {
          var link = document.createElement('a');
          link.className = 'wl-msearch__result' + (className ? ' ' + className : '');
          link.href = href;
          link.setAttribute('data-wl-cta', cta);
          link.setAttribute('data-wl-term', term);
          return link;
        }

        if (queries.length) {
          var queryList = document.createElement('ul');
          queryList.className = 'wl-msearch__results-list';
          queryList.setAttribute('role', 'list');
          queries.forEach(function (query) {
            var item = document.createElement('li');
            var link = row(query.url || searchHref(base, query.text), 'nav-search-suggestion');
            link.innerHTML = SEARCH_ICON;
            var text = document.createElement('span');
            text.className = 'wl-msearch__result-text';
            text.textContent = query.text || '';
            link.appendChild(text);
            item.appendChild(link);
            queryList.appendChild(item);
          });
          box.appendChild(queryList);
        }

        if (products.length) {
          var heading = document.createElement('p');
          heading.className = 'wl-msearch__label';
          heading.textContent = 'Products';
          box.appendChild(heading);
          var productList = document.createElement('ul');
          productList.className = 'wl-msearch__results-list';
          productList.setAttribute('role', 'list');
          products.forEach(function (product) {
            var item = document.createElement('li');
            var link = row(product.url, 'nav-search-product', 'wl-msearch__result--product');
            // suggest.json sends image and featured_image as URL strings; older payloads nest the URL.
            var featured = product.featured_image;
            var src = product.image || (typeof featured === 'string' ? featured : featured && featured.url);
            if (src) {
              var img = document.createElement('img');
              img.src = thumbUrl(src);
              img.width = 44;
              img.height = 44;
              img.alt = '';
              img.loading = 'lazy';
              link.appendChild(img);
            }
            var copy = document.createElement('span');
            copy.className = 'wl-msearch__result-copy';
            var title = document.createElement('span');
            title.className = 'wl-msearch__product-title';
            title.textContent = product.title || '';
            copy.appendChild(title);
            var price = formatPrice(product.price);
            if (price) {
              var priceEl = document.createElement('span');
              priceEl.className = 'wl-msearch__product-price';
              priceEl.style.display = 'block';
              priceEl.textContent = price;
              copy.appendChild(priceEl);
            }
            link.appendChild(copy);
            item.appendChild(link);
            productList.appendChild(item);
          });
          box.appendChild(productList);
        }

        if (!queries.length && !products.length) {
          var empty = document.createElement('p');
          empty.className = 'wl-msearch__empty';
          empty.textContent = 'No results for \u201c' + term + '\u201d.';
          box.appendChild(empty);
        }

        var all = document.createElement('a');
        all.className = 'wl-msearch__all';
        all.href = searchHref(base, term);
        all.setAttribute('data-wl-cta', 'nav-search-all');
        all.setAttribute('data-wl-term', term);
        all.textContent = 'See all results for \u201c' + term + '\u201d';
        box.appendChild(all);

        box.hidden = false;
        view.setAttribute('data-searching', '');
        if (status) {
          status.textContent = products.length || queries.length
            ? products.length + ' products and ' + queries.length + ' suggestions'
            : 'No results';
        }
      }

      onClick(event) {
        var target = event.target;
        if (!target || !target.closest) return;
        var el;

        if ((el = target.closest('[data-wl-drawer-parent]'))) {
          this.togglePanel(el);
          return;
        }
        // Back from the Bottle Shop panel to the full list. Closing the panel hides the back arrow
        // that holds focus; the row it came from takes it a frame later, once it is on screen again.
        if (target.closest('[data-wl-drawer-back]')) {
          var openRow = this.querySelector('[data-wl-drawer-parent][aria-expanded="true"]');
          this.closePanels();
          if (openRow) requestAnimationFrame(function () { openRow.focus(); });
          return;
        }
        if ((el = target.closest('[data-wl-drawer-acc]'))) {
          this.toggleAccordion(el);
          return;
        }
        // The in-drawer hamburger steps back: search to the menu, the menu closes. Inside the Bottle
        // Shop panel it is hidden and the back arrow takes its place.
        if (target.closest('[data-wl-drawer-burger]')) {
          if (this.view === 'search') this.setView('menu', true);
          else this.hide();
          return;
        }
        if (target.closest('[data-wl-search-cancel]')) {
          this.hide();
          return;
        }
        if ((el = target.closest('[data-wl-recent-remove]'))) {
          forgetSearch(el.getAttribute('data-wl-recent-remove'));
          this.renderRecent();
          if (this.searchInput) this.searchInput.focus();
          return;
        }
        if (target.closest('[data-wl-recent-clear]')) {
          writeRecent([]);
          this.renderRecent();
          if (this.searchInput) this.searchInput.focus();
          return;
        }
        if ((el = target.closest('[data-wl-cta="nav-search-popular"], [data-wl-cta="nav-search-recent"], [data-wl-cta="nav-search-suggestion"], [data-wl-cta="nav-search-product"], [data-wl-cta="nav-search-all"]'))) {
          rememberSearch(el.getAttribute('data-wl-term') || el.textContent);
          return;
        }
        // A link to a spot on the page already open (How it works on the quiz landing page)
        // would leave the drawer over the page it scrolled.
        if ((el = target.closest('a[href]'))) {
          var url;
          try { url = new URL(el.href, window.location.href); } catch (e) { return; }
          if (url.hash && url.origin === window.location.origin && url.pathname === window.location.pathname) this.hide();
        }
      }

      onSubmit(event) {
        var form = event.target;
        if (!form || !form.matches || !form.matches('[data-wl-search-form]')) return;
        var input = this.searchInput;
        var term = input ? input.value.replace(/\s+/g, ' ').trim() : '';
        if (!term) {
          event.preventDefault();
          if (input) input.focus();
          return;
        }
        rememberSearch(term);
        announce('search-submit', { query: term.slice(0, 80), query_length: term.length, surface: 'drawer' });
      }
    }

    window.customElements.define('wl-nav-drawer', WlNavDrawer);
  }

  function initAccountMenu(root) {
    if (!root || root.hasAttribute('data-wl-account-ready')) return;
    var button = root.querySelector('[data-wl-account-btn]');
    var menu = root.querySelector('[data-wl-account-menu]');
    if (!button || !menu) return;
    root.setAttribute('data-wl-account-ready', '');

    var closeTimer = null;
    var lastPointer = '';

    function isOpen() {
      return !menu.hidden;
    }

    // Escape is read on document while the menu is open: a menu opened by hovering has no focus
    // inside it, so a listener on the root never heard the key.
    function onEscape(event) {
      if (event.key !== 'Escape' || !isOpen()) return;
      var focusInside = root.contains(document.activeElement);
      event.preventDefault();
      setOpen(false);
      if (focusInside) button.focus();
    }

    function setOpen(open, trigger) {
      clearTimeout(closeTimer);
      if (open === isOpen()) return;
      menu.hidden = !open;
      button.setAttribute('aria-expanded', open ? 'true' : 'false');
      root.classList.toggle('is-open', open);
      if (open) {
        document.addEventListener('keydown', onEscape);
        announce('account-open', { surface: 'desktop', state: root.getAttribute('data-wl-account-state') || null, trigger: trigger || null });
      } else {
        document.removeEventListener('keydown', onEscape);
      }
    }

    root.addEventListener('pointerenter', function (event) {
      if (event.pointerType !== 'mouse') return;
      setOpen(true, 'hover');
    });
    root.addEventListener('pointerleave', function (event) {
      if (event.pointerType !== 'mouse') return;
      clearTimeout(closeTimer);
      closeTimer = setTimeout(function () {
        if (!root.contains(document.activeElement) || !document.activeElement.matches(':focus-visible')) setOpen(false);
      }, 150);
    });
    button.addEventListener('pointerdown', function (event) {
      lastPointer = event.pointerType || '';
    });
    // The trigger is a link (it still goes to the account page before this script runs), and
    // links only answer Enter; Space has to toggle it like the button it acts as.
    button.addEventListener('keydown', function (event) {
      if (event.key !== ' ' && event.key !== 'Spacebar') return;
      event.preventDefault();
      setOpen(!isOpen(), 'keyboard');
    });
    button.addEventListener('click', function (event) {
      event.preventDefault();
      // A mouse has already opened it by hovering; its click keeps it open instead of closing it.
      if (lastPointer === 'mouse' && event.detail > 0) setOpen(true, 'click');
      else setOpen(!isOpen(), event.detail > 0 ? 'tap' : 'keyboard');
      lastPointer = '';
    });
    root.addEventListener('focusout', function (event) {
      if (!isOpen()) return;
      if (event.relatedTarget && root.contains(event.relatedTarget)) return;
      if (root.matches(':hover')) return;
      setOpen(false);
    });
    document.addEventListener('click', function (event) {
      if (isOpen() && !root.contains(event.target)) setOpen(false);
    });
  }

  function initAccountMenus(scope) {
    (scope || document).querySelectorAll('[data-wl-account]').forEach(initAccountMenu);
  }

  if (window.customElements.get('drawer-element')) defineDrawer();
  else window.customElements.whenDefined('drawer-element').then(defineDrawer);

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { initAccountMenus(); });
  else initAccountMenus();
  document.addEventListener('shopify:section:load', function (event) { initAccountMenus(event.target); });
})();
